import { motion } from 'framer-motion';
import { useState } from 'react';
import { 
  Phone, 
  Mail, 
  MapPin,
  Clock,
  ArrowRight,
  MessageSquare,
  Users,
  Shield,
  Zap
} from 'lucide-react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { sendInvestmentInquiryEmail } from '../services/emailService';

function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
    investmentAmount: '',
    preferredPlan: 'standard'
  });

  const [status, setStatus] = useState({ type: '', message: '' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ type: '', message: '' });

    // Optionally, add validation here

    const result = await sendInvestmentInquiryEmail(formData);

    if (result.success) {
      setStatus({ type: 'success', message: 'Your inquiry has been sent! We will contact you soon.' });
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: '',
        investmentAmount: '',
        preferredPlan: 'standard'
      });
    } else {
      setStatus({ type: 'error', message: 'There was an error sending your inquiry. Please try again later.' });
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const contactInfo = [
    {
      icon: Phone,
      title: "Investment Hotline",
      details: ["+1 (269) 207-6788"],
      description: "24/7 Priority Support for Investors",
      color: "blue"
    },
    {
      icon: Mail,
      title: "Email Support",
      details: ["support@onchainvestcapital.com"],
      description: "Response within 2 hours for investment inquiries",
      color: "purple"
    },
    {
      icon: MapPin,
      title: "Investment Office",
      details: ["277 S Rose St, Kalamazoo, MI 49007"],
      description: "Schedule a private consultation",
      color: "green"
    },
    {
      icon: Clock,
      title: "Trading Hours",
      details: ["24/7 Online Platform Access", "Office Hours: Mon-Fri 9AM-6PM EST"],
      description: "Investment processing available 24/7",
      color: "cyan"
    }
  ];

  const features = [
    {
      icon: Shield,
      title: "Secure Communication",
      description: "All inquiries are encrypted and secure"
    },
    {
      icon: Users,
      title: "Expert Team",
      description: "Get advice from certified investment professionals"
    },
    {
      icon: Zap,
      title: "Quick Response",
      description: "We respond to all inquiries within 2 hours"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white">
      <Navigation />
      
      {/* Hero Section - Unique Design */}
      <section className="relative pt-8 md:pt-20 pb-16 overflow-hidden">
        {/* Background Elements */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900"></div>
        <div className="absolute inset-0 bg-gradient-to-r from-blue-600/10 to-purple-600/10"></div>
        
        {/* Floating Elements */}
        <div className="absolute top-20 right-10 w-32 h-32 bg-blue-500/20 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 left-10 w-40 h-40 bg-purple-500/20 rounded-full blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 right-1/4 w-24 h-24 bg-cyan-500/20 rounded-full blur-2xl animate-pulse delay-500"></div>
        
        <div className="container mx-auto px-6 relative z-10">
          <div className="text-center max-w-4xl mx-auto py-20">
            <motion.div
              className="inline-block mb-6 px-6 py-3 rounded-full bg-gradient-to-r from-blue-600/20 to-purple-600/20 text-blue-400 font-semibold text-sm border border-blue-500/30"
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.1, duration: 0.6 }}
            >
              💬 Let's Start Your Investment Journey
            </motion.div>
            
            <motion.h1 
              className="text-4xl md:text-6xl font-bold mb-8 leading-tight"
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.8 }}
            >
              <span className="bg-gradient-to-r from-blue-400 via-purple-400 to-cyan-400 bg-clip-text text-transparent">
                Get Expert
              </span>
              <br />
              <span className="text-white">
                Investment Guidance
              </span>
            </motion.h1>
            
            <motion.p 
              className="text-lg md:text-xl mb-12 text-gray-300 leading-relaxed max-w-3xl mx-auto"
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.4, duration: 0.8 }}
            >
              Ready to earn up to 20x returns in 21 days? Our investment experts are here to guide you through our high-yield investment plans and help you maximize your wealth potential.
            </motion.p>

            {/* Features Grid */}
            <motion.div 
              className="grid md:grid-cols-3 gap-6 mb-12"
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.6, duration: 0.8 }}
            >
              {features.map((feature, index) => {
                const Icon = feature.icon;
                return (
                  <div key={index} className="card-glass p-6 rounded-2xl text-center">
                    <Icon className="mx-auto mb-4 text-blue-400" size={32} />
                    <h3 className="text-lg font-semibold text-white mb-2">{feature.title}</h3>
                    <p className="text-gray-400 text-sm">{feature.description}</p>
                  </div>
                );
              })}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Contact Info Cards - Left Column */}
            <motion.div 
              className="space-y-8"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4, duration: 0.8 }}
            >
              <div>
                <h2 className="text-4xl font-bold mb-8 bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                  Get in Touch
                </h2>
                <p className="text-lg text-gray-300 mb-8">
                  Our investment experts are ready to help you start your wealth-building journey. Choose your preferred method of contact.
                </p>
              </div>
                
                <div className="space-y-6">
                {contactInfo.map((item, index) => {
                  const Icon = item.icon;
                  return (
                    <motion.div
                      key={index}
                      className="card-glass p-6 rounded-2xl border-l-4 border-blue-500 card-hover"
                      whileHover={{ x: 5 }}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.1 * index, duration: 0.5 }}
                    >
                      <div className="flex items-start space-x-4">
                        <div className={`bg-gradient-to-r from-${item.color}-600 to-${item.color}-500 p-3 rounded-xl`}>
                          <Icon className="h-6 w-6 text-white" />
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-white mb-2">{item.title}</h3>
                          {item.details.map((detail, idx) => (
                            <p key={idx} className="text-gray-300 text-sm mb-1">{detail}</p>
                          ))}
                          <p className="text-sm text-blue-400 mt-2 font-medium">{item.description}</p>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
                </div>

              {/* Map Section */}
                <motion.div 
                className="card-glass rounded-2xl overflow-hidden shadow-2xl"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.8, duration: 0.8 }}
      >
                  <iframe
                    title="Office Location"
                  className="w-full h-[300px]"
                    src="https://www.google.com/maps?q=277+S+Rose+St,+Kalamazoo,+MI+49007&output=embed"
                    loading="lazy"
                    referrerPolicy="no-referrer-when-allowed"
                    allowFullScreen
                  />
                </motion.div>
            </motion.div>
            
            {/* Contact Form Section - Right Column */}
            <motion.div 
              className="card-glass p-8 rounded-2xl"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.6, duration: 0.8 }}
            >
              <h3 className="text-2xl font-bold text-white mb-6">Send Investment Inquiry</h3>
              
              {status.message && (
                <div className={`mb-6 p-4 rounded-lg ${
                  status.type === 'success' 
                    ? 'bg-green-500/20 border border-green-500/30 text-green-400' 
                    : 'bg-red-500/20 border border-red-500/30 text-red-400'
                }`}>
                  {status.message}
            </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Full Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                      className="input-glass w-full px-4 py-3 rounded-xl"
                      placeholder="Enter your full name"
                  />
                </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Email Address *</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                      className="input-glass w-full px-4 py-3 rounded-xl"
                      placeholder="Enter your email"
                  />
                </div>
              </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Phone Number</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                      onChange={handleChange}
                      className="input-glass w-full px-4 py-3 rounded-xl"
                      placeholder="Enter your phone number"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Investment Amount</label>
                    <select
                      name="investmentAmount"
                      value={formData.investmentAmount}
                      onChange={handleChange}
                      className="input-glass w-full px-4 py-3 rounded-xl"
                    >
                      <option value="">Select amount range</option>
                      <option value="1k-5k">$1,000 - $5,000</option>
                      <option value="5k-10k">$5,000 - $10,000</option>
                      <option value="10k-25k">$10,000 - $25,000</option>
                      <option value="25k-50k">$25,000 - $50,000</option>
                      <option value="50k+">$50,000+</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Preferred Investment Plan</label>
                  <select
                    name="preferredPlan"
                    value={formData.preferredPlan}
                    onChange={handleChange}
                    className="input-glass w-full px-4 py-3 rounded-xl"
                  >
                    <option value="Standard Plan">Standard Plan</option>
                    <option value="Premium Plan">Premium Plan</option>
                    <option value="Advanced Plan">Advanced Plan</option>
                    <option value="Platinum Plan">Platinum Plan</option>
                    <option value="Diamond Plan">Diamond Plan</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Subject *</label>
                  <input
                    type="text"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    className="input-glass w-full px-4 py-3 rounded-xl"
                    placeholder="Investment inquiry subject"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Message *</label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                    required
                    rows={6}
                    className="input-glass w-full px-4 py-3 rounded-xl resize-none"
                    placeholder="Tell us about your investment goals and any questions you have..."
                />
              </div>

                <motion.button
                  type="submit"
                  className="w-full bg-gradient-to-r from-blue-600 to-purple-600 px-8 py-4 rounded-xl text-lg font-semibold hover:from-blue-700 hover:to-purple-700 transform hover:scale-105 transition-all duration-300 shadow-2xl flex items-center justify-center space-x-2"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                >
                  <span>Send Investment Inquiry</span>
                  <ArrowRight className="h-5 w-5" />
                </motion.button>
              </form>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-purple-600">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to Start Investing?</h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Join thousands of successful investors who have already started their wealth-building journey with Onchainvest Capital.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="bg-white text-slate-900 px-8 py-4 rounded-xl text-lg font-semibold hover:bg-gray-100 transform hover:scale-105 transition-all duration-300 shadow-2xl">
              Start Investing Now
            </button>
            <button className="border-2 border-white px-8 py-4 rounded-xl text-lg font-semibold hover:bg-white/10 transition-all duration-300">
              View Investment Plans
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default Contact; 